import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	INavHelp,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	TileComponent,
	ButtonComponent,
	FormGroupComponent,
	InputComponent,
} from '@tecalisdev/infinity';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { IModalClose } from 'src/app/core/models/modal-confirmation.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { DialogConfirmationService } from 'src/app/core/services/dialog-confirmation.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { IMaster } from 'src/app/shared/models/master.models';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { OperationService } from 'src/app/shared/services/step.service';
import {
	CONNECTION_TYPE,
	IScriptureConfig,
	IConnectionOperation,
	IFinishOperation,
	IOperation,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from 'src/app/user-identification/models/user-identification';
import { ConnectionsHelperService } from 'src/app/user-identification/services/connections/connections-helper.service';
import { ConnectionsService } from 'src/app/user-identification/services/connections/connections.service';
import { ProcessService } from 'src/app/user-identification/services/process.service';

@Component({
	selector: 'app-scripture',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslatePipe,
		PwcsLayoutComponent,
		ProgressComponent,
		TileComponent,
		ButtonComponent,
		FormGroupComponent,
		InputComponent,
		NavHelpComponent,
		ModalScanQrComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './scripture.component.html',
	styleUrls: ['./scripture.component.scss'],
})
export class ScriptureComponent implements OnInit {
	private navHelpService = inject(NavHelpService);
	private languagePickerUi = inject(LanguagePickerUiService);
	trans = inject(TranslateService);
	dialogConfirmationService = inject(DialogConfirmationService);
	public auth = inject(AuthService);
	loadingService = inject(LoadingService);
	connectionsService = inject(ConnectionsService);
	reactiveFormService = inject(ReactiveFormsService);
	operationService = inject(OperationService);

	@Input()
	get operations(): IOperation[] {
		return this._operations;
	}
	set operations(operations: IOperation[]) {
		this._operations = operations;
		const chooseOperation = operations.find((op) => op.type === OPERATION_TYPE.CHOOSE);
		const scriptureOperation: any = operations.find(
			(op) => op.type === OPERATION_TYPE.CONNECTIONS && op.config?.type === CONNECTION_TYPE.SCRIPTURE,
		);
		/*
			- Si tengo la operacion choose no muestro el tutorial y realizo la
			comprobaciones por si tengo ya los datos necesarios e iniciar la operación
			- Si no tengo la operacion choose y tengo el campo que indica que se
			debe evaluar financieramiente el documento muestro el tutorial
		*/
		if (chooseOperation) {
			this.tutorial = false;
			this.initOperationIfHasCif();
		} else {
			if (scriptureOperation?.config?.evaluation) {
				this.tutorial = true;
			} else {
				this.tutorial = false;
				this.initOperationIfHasCif();
			}
		}
	}
	@Input() operation: IConnectionOperation;

	public OPERATION_TYPE: any = OPERATION_TYPE;
	public MODE = MODE;
	private _operations: IOperation[];
	public errorType: 'PRE_2009' | 'COMPANY_NOT_ACTIVE' | 'NOT-SCRIPTURE' | 'PERSON_NOT_MATCHED' | 'ERROR';
	public form: FormGroup = new FormGroup({
		cif: new FormControl('', Validators.required),
	});
	public formCifComplete: boolean = false;
	private initOperation: boolean = false;
	public tutorial: boolean = false;
	public navsHelp: Array<INavHelp> = this.navHelpService.connectionsNavsHelp;
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getConnectionsTutorialNavsHelp();
	public isVisibleScan: boolean = false;

	/** True si el workflow permite adjuntar escrituras como fallback. */
	public get hasManualFallback(): boolean {
		return (this.operation?.config as IScriptureConfig | undefined)?.fallback?.type === MODE.MANUAL;
	}

	/**
	 * Clave i18n de descripción de error: con CTA de adjuntar solo si hay fallback manual.
	 */
	public errorDescriptionKey(
		key: 'PRE_2009' | 'INACTIVE' | 'NOT_SCRIPTURE' | 'PERSON_NOT_MATCHED',
	): string {
		const base = `CONNECTIONS.SCRIPTURE.ERROR.${key}_DESCRIPTION`;
		return this.hasManualFallback ? base : `${base}_NO_MANUAL`;
	}

	constructor(
		public designService: DesignService,
		public progressService: ProgressService,
		private masterService: MasterService,
		private processService: ProcessService,
		private connectionsHelper: ConnectionsHelperService,
	) {}

	ngOnInit(): void {
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		this.connectionsHelper.initNumberOfTries(this.operation);
	}

	public initScriptureRobot(): void {
		this.connectionsHelper.initNumberOfTries(this.operation);
		if (this.operation?.config) this.operation.config.numberOfTries++;
		this.loadingService.show(this.trans.instant('CONNECTIONS.SCRIPTURE.LOADING_INIT'));
		this.connectionsService
			.scriptureInit({ cif: this.form.get('cif')?.value || undefined, uuid_config: this.operation.uuid })
			.subscribe({
				next: (response: any) => {
					this.formCifComplete = true;
					if (response.result === OPERATION_STATUS.KO) {
						this.operations.forEach((op: IOperation) => {
							if (op.type !== OPERATION_TYPE.FINISH) {
								op.completed = true;
							}
						});
					}
					const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
					if (found) {
						(found as IFinishOperation).config.status = response.result;
					}
					this.operation.completed = true;
				},
				error: (error: HttpErrorResponse) => {
					const errPayload = error?.error?.error as
						| { error_code?: string | number; business_error_code?: string | number; msg?: string }
						| undefined;
					const errorCode = errPayload?.error_code?.toString();
					const businessCode = errPayload?.business_error_code?.toString();
					const msg = errPayload?.msg;

					switch (errorCode) {
						case ERROR_CODE.FALLBACK_MANUAL:
							this.confirmManualUpload();
							this.loadingService.hide();
							return;
						case ERROR_CODE.KO_LIBREBOR:
						case ERROR_CODE.SCRIPTURE_PERSON_NOT_MATCHED:
						case ERROR_CODE.SCRIPTURE_COMPANY_NOT_ACTIVE:
						case ERROR_CODE.SCRIPTURE_QUERY_ERROR:
							this.errorType = this.resolveScriptureBusinessErrorType(errorCode, msg);
							break;
						case ERROR_CODE.MAXIMUM_TRIES_EXCEEDED:
							this.errorType = this.resolveScriptureBusinessErrorType(
								businessCode ?? errorCode,
								msg,
							);
							break;
						case ERROR_CODE.SCRIPTURE_NEVER_RETRY:
							this.errorType = 'PRE_2009';
							break;
						case ERROR_CODE.SCRIPTURE_NOT_FOUND:
							this.errorType = 'NOT-SCRIPTURE';
							break;
						default:
							this.errorType = 'ERROR';
							break;
					}
					this.verificationFail(error);
					this.loadingService.hide();
				},
				complete: () => {
					this.loadingService.hide();
				},
			});
	}

	public changeToManualUpload(): void {
		const dialogMessage = {
			title: this.trans.instant('CONNECTIONS.SCRIPTURE.MANUAL.TITLE'),
			description: this.trans.instant('CONNECTIONS.SCRIPTURE.MANUAL.DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('GENERAL.BUTTON.YES_ATTACH'),
			buttonSecondaryLabel: this.trans.instant('GENERAL.BUTTON.CANCEL'),
		};
		this.dialogConfirmationService.show(dialogMessage).subscribe((event: IModalClose) => {
			if (event === 'buttonPrimary') {
				this.confirmManualUpload();
			}

			this.dialogConfirmationService.hide();
		});
	}

	/**
	 * NIF-800: mapea código de negocio SCRIPTURE (y legado 2019) a la pantalla de error.
	 */
	public resolveScriptureBusinessErrorType(
		code?: string | null,
		msg?: string | null,
	): 'PRE_2009' | 'COMPANY_NOT_ACTIVE' | 'NOT-SCRIPTURE' | 'PERSON_NOT_MATCHED' | 'ERROR' {
		const normalized = code?.toString();
		switch (normalized) {
			case ERROR_CODE.SCRIPTURE_PERSON_NOT_MATCHED:
				return 'PERSON_NOT_MATCHED';
			case ERROR_CODE.SCRIPTURE_COMPANY_NOT_ACTIVE:
				return 'COMPANY_NOT_ACTIVE';
			case ERROR_CODE.SCRIPTURE_QUERY_ERROR:
				return 'ERROR';
			case ERROR_CODE.KO_LIBREBOR: {
				const token = (msg ?? '').trim();
				if (
					token === 'PERSON_NOT_MATCHED' ||
					token === 'COMPANY_NOT_ACTIVE' ||
					token === 'ERROR'
				) {
					return token;
				}
				return 'ERROR';
			}
			default: {
				const token = (msg ?? '').trim();
				if (
					token === 'PERSON_NOT_MATCHED' ||
					token === 'COMPANY_NOT_ACTIVE' ||
					token === 'ERROR'
				) {
					return token;
				}
				return 'ERROR';
			}
		}
	}

	private confirmManualUpload(): void {
		this.loadingService.show(this.trans.instant('GENERAL.LOADING.CHANGE_TO_MANUAL'));
		this.operation.completed = true;
		const positionOperation = this.operations.findIndex((op: IOperation) => op.uuid === this.operation.uuid);
		if (positionOperation !== -1) {
			this.operations.splice(
				positionOperation,
				0,
				this.connectionsHelper.getConnectionDocType(CONNECTION_TYPE.SCRIPTURE, this.operation),
			);
			this.operationService.setOperations(this.operations);
		}
	}

	private verificationFail(error: HttpErrorResponse): void {
		const retry = error?.error?.retry ?? false;
		if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
			this.operation.completed = true;
			this.operations.forEach((op: IOperation) => {
				if (op.type !== OPERATION_TYPE.FINISH) {
					op.completed = true;
				}
			});
			const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
			if (foundFinish) {
				(foundFinish as IFinishOperation).config.status = error?.error?.result;
			}
		}
	}

	private initOperationIfHasCif(): void {
		const cif = (
			this.operations.find(
				(op) => op.type === OPERATION_TYPE.CONNECTIONS && op.config?.type === CONNECTION_TYPE.SCRIPTURE,
			)?.config as IScriptureConfig
		)?.cif;
		if (cif) {
			this.form.patchValue({
				cif: cif,
			});
		}
		this.initScriptureRobot();
	}

	public finishTutorial(): void {
		this.tutorial = false;
		this.initOperationIfHasCif();
	}

	/** Barra de ayuda: QR, idioma y ayuda contextual (tutorial solo QR + idioma). */
	public navHelpChange(action: INavHelp): void {
		const actions: Record<string, () => void> = {
			[NavHelpAction.QR]: () => {
				this.isVisibleScan = true;
			},
			[NavHelpAction.LANGUAGE]: () => {
				this.languagePickerUi.open();
			},
			[NavHelpAction.HELP]: () => {
				this.openScriptureHelpDialog();
			},
		};
		actions[action.id]?.();
	}

	private openScriptureHelpDialog(): void {
		this.dialogConfirmationService
			.show({
				title: this.trans.instant('GENERAL.NAV_HELP.HELP'),
				description: this.trans.instant('CONNECTIONS.SCRIPTURE.SUBTITLE'),
				buttonPrimaryLabel: this.trans.instant('GENERAL.BUTTON.OK'),
			})
			.subscribe(() => {
				this.dialogConfirmationService.hide();
			});
	}
}
